import { toRaw } from 'vue';
import axiosInstance from '@/services/interceptor.services';
import { restaurantId } from '@/refs/common.refs';
import type { ITranslationExportParams } from '@/types/translation.types';

interface ITranslateResult {
  language: string;
  code: string;
  translatedText: string;
}
interface ITranslateResponse {
  data: ITranslateResult[];
  message: string;
}

export const TranslationService = {
  /**
   * Traduit un texte vers toutes les langues disponibles via notre backend
   * (POST translation). Retourne un objet {fr, en, nl} ; les langues absentes
   * de la réponse ou en erreur restent des chaînes vides.
   */
  translateText: async (
    text: string,
    sourceLanguage?: 'fr' | 'en' | 'nl',
  ): Promise<{ fr: string; en: string; nl: string }> => {
    const result = { fr: '', en: '', nl: '' };
    if (sourceLanguage) result[sourceLanguage] = text;
    try {
      const res = await axiosInstance.post<ITranslateResponse>('translation', {
        text,
        ...(sourceLanguage ? { sourceLanguage } : {}),
      });
      for (const r of res.data?.data ?? []) {
        if (r.code === 'fr' || r.code === 'en' || r.code === 'nl') {
          result[r.code] = r.translatedText;
        }
      }
    } catch {
      /* les champs restent vides, l'utilisateur peut saisir manuellement */
    }
    return result;
  },
  exportTranslations: async (params?: Partial<ITranslationExportParams>) => {
    const storeId = toRaw(restaurantId.value);
    const query = new URLSearchParams();
    if (storeId) query.append('storeId', storeId);
    query.append('sourceLanguage', params?.sourceLanguage ?? 'fr');
    (params?.languages ?? ['fr', 'en', 'nl']).forEach((lang) =>
      query.append('languages', lang),
    );
    params?.itemTypes?.forEach((t) => query.append('itemTypes', t));
    params?.statuses?.forEach((s) => query.append('statuses', s));
    if (params?.updatedAfter) query.append('updatedAfter', params.updatedAfter);
    if (params?.limit !== undefined)
      query.append('limit', String(params.limit));

    return axiosInstance.get<Blob>(`translations/export?${query.toString()}`, {
      responseType: 'blob',
    });
  },

  importTranslations: async (
    file: File,
    onProgress?: (percent: number) => void,
  ) => {
    const formData = new FormData();
    formData.append('file', file);

    return axiosInstance.post('translations/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (evt.total) {
          onProgress?.(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    });
  },
};
